import { Alert, ConfigProvider, DatePicker, Form, Input, InputNumber, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { WaterMeterRepo } from "@/core/repos/tables/water-meter.repo";
import { ChangeWaterMeterModel } from "@/core/models/water-meter-change.model";
import {
    WaterMeterChangeReason,
    getWaterMeterChangeReasonDisplay,
} from "@/core/enums/global.enums";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { DEF_DATE_FORMAT, toApiTimestamp } from "@/app/home/utils/global.utils";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import dayjs from "dayjs";

const waterMeterRepo = container.get(WaterMeterRepo);
const contractRepo = container.get(ContractRepo);

interface ChangeMeterModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    data: ContractModel;
}

const INPUT_WIDTH = 450;

const REASON_OPTIONS = (Object.keys(WaterMeterChangeReason) as (keyof typeof WaterMeterChangeReason)[]).map((key) => ({
    label: getWaterMeterChangeReasonDisplay(WaterMeterChangeReason[key]),
    value: WaterMeterChangeReason[key],
}));

export const ChangeMeterModal: React.FC<ChangeMeterModalProps> = ({ isModalOpen, setIsModalOpen, data }) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<ChangeWaterMeterModel>();
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = Modal.useModal();

    const lastWaterMeterNumber = data.lastWaterMeterNumber ?? 0;
    const canEdit = permissionStore.page.edit;

    const handleDataChange = () => {
        form.setFieldsValue({
            waterUserId: data.id,
            oldWaterMeterCode: data.waterMeterCode ?? "",
            removedIndex: lastWaterMeterNumber,
            newWaterMeterCode: "",
            headIndex: 0,
            changeDate: dayjs().toISOString(),
            waterMeterMaxIndex: data.waterMeterMaxIndex ?? null,
            reason: WaterMeterChangeReason.Replacement,
            note: "",
        });
    };

    useEffect(() => {
        if (isModalOpen) {
            handleDataChange();
        }
    }, [isModalOpen, data]);

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const doSubmit = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            const payload: ChangeWaterMeterModel = {
                waterUserId: data.id,
                oldWaterMeterCode: data.waterMeterCode ?? "",
                removedIndex: Number(formData.removedIndex),
                newWaterMeterCode: (formData.newWaterMeterCode ?? "").trim(),
                headIndex: Number(formData.headIndex ?? 0),
                changeDate: toApiTimestamp(formData.changeDate) ?? dayjs().toISOString(),
                waterMeterMaxIndex:
                    formData.waterMeterMaxIndex != null ? Number(formData.waterMeterMaxIndex) : undefined,
                reason: formData.reason ?? WaterMeterChangeReason.Replacement,
                note: formData.note ?? "",
            };

            const response = await waterMeterRepo.changeMeter(payload);
            if (response?.code === "00") {
                Modal.success({ content: "Đã thay đồng hồ thành công." });
                contractRepo.refresh();
                setIsModalOpen(false);
            } else {
                Modal.error({
                    content: response?.errorMessage ?? "Thay đồng hồ thất bại.",
                });
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const handleSave = async () => {
        try {
            const formData = await form.validateFields();
            const removedIndex = Number(formData.removedIndex);
            const newCode = (formData.newWaterMeterCode ?? "").trim();
            const oldCode = data.waterMeterCode ?? "";

            // Client-side validation mirrors backend rules
            if (!newCode) {
                Modal.error({ content: "Vui lòng nhập mã đồng hồ mới." });
                return;
            }
            if (newCode === oldCode) {
                Modal.error({ content: "Mã đồng hồ mới phải khác mã đồng hồ cũ." });
                return;
            }
            if (removedIndex < lastWaterMeterNumber) {
                Modal.error({
                    content: `Chỉ số tháo (${removedIndex}) không được nhỏ hơn chỉ số lần đọc gần nhất (${lastWaterMeterNumber}).`,
                });
                return;
            }
            if (Number(formData.headIndex ?? 0) < 0) {
                Modal.error({ content: "Chỉ số đầu của đồng hồ mới không hợp lệ." });
                return;
            }

            const changeDate = formData.changeDate ? dayjs(formData.changeDate) : dayjs();
            Modal.confirm({
                title: "Xác nhận thay đồng hồ",
                content: `Sau khi xác nhận, mã đồng hồ của hộ sẽ chuyển sang DH mới. Kỳ ${changeDate.month() + 1}/${changeDate.year()} sẽ tính hoá đơn gộp 2 đoạn. Tiếp tục?`,
                okText: "Xác nhận thay",
                cancelText: "Huỷ",
                onOk: doSubmit,
            });
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                width={700}
                style={{ top: 20 }}
                styles={{
                    mask: {
                        backdropFilter: "blur(2px)",
                        backgroundColor: "var(--primary-mask)",
                    },
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={true} loading={loading} onClick={handleSave} />
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">THAY ĐỒNG HỒ NƯỚC</p>
                </div>

                <div className="flex flex-row justify-between px-4 mt-4 mb-2">
                    <div>
                        <p className="text-sm text-gray-500">Mã khách hàng</p>
                        <p className="font-semibold">{data.code}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Tên khách hàng</p>
                        <p className="font-semibold">{data.name}</p>
                    </div>
                </div>

                <div className="flex flex-row justify-center mt-2">
                    <Form<ChangeWaterMeterModel>
                        name="change-meter"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={!canEdit}
                    >
                        <Form.Item<ChangeWaterMeterModel>
                            label="Đồng hồ hiện tại"
                            name="oldWaterMeterCode"
                        >
                            <Input disabled style={{ width: INPUT_WIDTH }} />
                        </Form.Item>

                        <Form.Item label="Chỉ số lần đọc gần nhất">
                            <InputNumber
                                disabled
                                style={{ width: INPUT_WIDTH }}
                                value={lastWaterMeterNumber}
                            />
                        </Form.Item>

                        <Form.Item<ChangeWaterMeterModel>
                            label="Chỉ số tháo đồng hồ cũ"
                            name="removedIndex"
                            rules={[{ required: true, message: "Vui lòng nhập chỉ số tháo" }]}
                        >
                            <InputNumber
                                min={0}
                                style={{ width: INPUT_WIDTH }}
                                placeholder="Nhập chỉ số tháo đồng hồ cũ"
                            />
                        </Form.Item>

                        <Form.Item<ChangeWaterMeterModel>
                            label="Mã đồng hồ mới"
                            name="newWaterMeterCode"
                            rules={[{ required: true, message: "Vui lòng nhập mã đồng hồ mới" }]}
                        >
                            <Input style={{ width: INPUT_WIDTH }} placeholder="Nhập mã đồng hồ mới" />
                        </Form.Item>

                        <Form.Item<ChangeWaterMeterModel>
                            label="Chỉ số đầu đồng hồ mới"
                            name="headIndex"
                            rules={[{ required: true, message: "Vui lòng nhập chỉ số đầu" }]}
                        >
                            <InputNumber
                                min={0}
                                style={{ width: INPUT_WIDTH }}
                                placeholder="Nhập chỉ số đầu (thường = 0)"
                            />
                        </Form.Item>

                        <Form.Item<ChangeWaterMeterModel> label="Ngày thay" name="changeDate"
                            getValueProps={(i) => ({ value: dayjsPipe(i) })}
                        >
                            <DatePicker
                                format={DEF_DATE_FORMAT}
                                style={{ width: INPUT_WIDTH }}
                            />
                        </Form.Item>

                        <Form.Item<ChangeWaterMeterModel> label="Hạn đọc tối đa" name="waterMeterMaxIndex">
                            <InputNumber
                                min={0}
                                style={{ width: INPUT_WIDTH }}
                                placeholder="Bỏ trống để giữ nguyên giá trị cũ"
                            />
                        </Form.Item>

                        <Form.Item<ChangeWaterMeterModel> label="Lý do" name="reason">
                            <Select
                                showSearch
                                style={{ width: INPUT_WIDTH }}
                                options={REASON_OPTIONS}
                                placeholder="Chọn lý do thay"
                            />
                        </Form.Item>

                        <Form.Item<ChangeWaterMeterModel> label="Ghi chú" name="note">
                            <Input.TextArea
                                style={{ width: INPUT_WIDTH }}
                                rows={3}
                                placeholder="Ghi chú thêm (tuỳ chọn)"
                            />
                        </Form.Item>

                        <Alert
                            type="info"
                            showIcon
                            message="Hoá đơn kỳ này sẽ tính gộp 2 đoạn"
                            description="Đoạn dùng của đồng hồ cũ (chỉ số tháo − chỉ số đầu kỳ) + đoạn dùng của đồng hồ mới (chỉ số cuối − chỉ số đầu). Hệ thống tự tính, bạn không cần cộng/trừ."
                            style={{ width: INPUT_WIDTH, marginBottom: 12 }}
                        />
                    </Form>
                </div>
            </Modal>
        </ConfigProvider>
    );
};
